import { ThemeKeysEnum } from '../api/models';
import { FontStyleModel } from '../models/font-style.model';

export class ThemeUtils {
	static hexToOklab(hex: string): {
		L: number;
		a: number;
		b: number;
		alpha: number;
	} {
		const rgba = this.hexToRgba(hex);
		if (!rgba) {
			throw new Error(`Invalid hex color: ${hex}`);
		}

		let r = rgba.r / 255;
		let g = rgba.g / 255;
		let b = rgba.b / 255;

		r = r <= 0.04045 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
		g = g <= 0.04045 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
		b = b <= 0.04045 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

		const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
		const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
		const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

		const l_ = Math.cbrt(l);
		const m_ = Math.cbrt(m);
		const s_ = Math.cbrt(s);

		const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
		const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
		const bVal = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

		return { L, a, b: bVal, alpha: rgba.a / 255 };
	}

	static oklabToHex(L: number, a: number, b: number, alpha: number): string {
		const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
		const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
		const s_ = L - 0.0894841775 * a - 1.291485548 * b;

		const l = l_ ** 3;
		const m = m_ ** 3;
		const s = s_ ** 3;

		const rLinear = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
		const gLinear = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
		const bLinear = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

		const rClamped = Math.min(Math.max(rLinear, 0), 1);
		const gClamped = Math.min(Math.max(gLinear, 0), 1);
		const bClamped = Math.min(Math.max(bLinear, 0), 1);

		const toSRGB = (linear: number) => {
			return linear <= 0.0031308
				? linear * 12.92
				: 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
		};

		const rSRGB = toSRGB(rClamped);
		const gSRGB = toSRGB(gClamped);
		const bSRGB = toSRGB(bClamped);

		const rgbR = Math.round(rSRGB * 255);
		const rgbG = Math.round(gSRGB * 255);
		const rgbB = Math.round(bSRGB * 255);
		const alphaByte = Math.round(alpha * 255);

		let hex = `#${this.componentToHex(rgbR)}${this.componentToHex(rgbG)}${this.componentToHex(rgbB)}`;
		if (alphaByte !== 255) {
			hex += this.componentToHex(alphaByte);
		}
		return hex;
	}

	static hexToRgba(hexString: string) {
		const hex = hexString.replace(/^#/, '');
		let r = 0,
			g = 0,
			b = 0,
			a = 255;

		switch (hex.length) {
			case 3:
				r = parseInt(hex[0] + hex[0], 16);
				g = parseInt(hex[1] + hex[1], 16);
				b = parseInt(hex[2] + hex[2], 16);
				break;
			case 6:
				r = parseInt(hex.substring(0, 2), 16);
				g = parseInt(hex.substring(2, 4), 16);
				b = parseInt(hex.substring(4, 6), 16);
				break;
			case 8:
				r = parseInt(hex.substring(0, 2), 16);
				g = parseInt(hex.substring(2, 4), 16);
				b = parseInt(hex.substring(4, 6), 16);
				a = parseInt(hex.substring(6, 8), 16);
				break;
			default:
				return;
		}

		return { r, g, b, a };
	}

	static rgbToHsl(r: number, g: number, b: number) {
		r /= 255;
		g /= 255;
		b /= 255;
		const max = Math.max(r, g, b),
			min = Math.min(r, g, b);
		let h = 0,
			s,
			l = (max + min) / 2;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;
				case g:
					h = (b - r) / d + 2;
					break;
				case b:
					h = (r - g) / d + 4;
					break;
			}
			h *= 60;
		} else {
			s = 0;
		}

		return { h: Math.round(h), s, l };
	}

	static hueToRgb(p: number, q: number, t: number): number {
		if (t < 0) t += 360;
		if (t > 360) t -= 360;
		if (t < 60) return p + (q - p) * (t / 60);
		if (t < 180) return q;
		if (t < 240) return p + (q - p) * ((240 - t) / 60);
		return p;
	}

	static hslToRgb(
		h: number,
		s: number,
		l: number,
	): { r: number; g: number; b: number } {
		let r, g, b;

		if (s === 0) {
			r = g = b = Math.round(l * 255);
		} else {
			const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
			const p = 2 * l - q;
			r = Math.round(ThemeUtils.hueToRgb(p, q, h + 120) * 255);
			g = Math.round(ThemeUtils.hueToRgb(p, q, h) * 255);
			b = Math.round(ThemeUtils.hueToRgb(p, q, h - 120) * 255);
		}
		return { r, g, b };
	}

	static componentToHex(c: number): string {
		const hex = c.toString(16);
		return hex.length === 1 ? '0' + hex : hex;
	}

	static rgbaToHex(r: number, g: number, b: number, a = 255) {
		let hex = `#${ThemeUtils.componentToHex(r)}${ThemeUtils.componentToHex(g)}${ThemeUtils.componentToHex(b)}`;

		if (a !== 255) {
			hex += ThemeUtils.componentToHex(a);
		}

		return hex;
	}

	static convertThemeKeyToCssVariable(key: string): string {
		return `--gs-${key
			.replace(/([a-z])([A-Z0-9])/g, '$1-$2')
			.replace(/([0-9])([A-Za-z])/g, '$1-$2')
			.toLowerCase()}`;
	}

	static convertCssVariableToThemeKey(
		cssVariable: string,
	): ThemeKeysEnum | undefined {
		const parts = cssVariable.replace('--gs-', '').split('-');
		const themeKeyString = parts
			.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
			.join('');
		return Object.values(ThemeKeysEnum).includes(
			themeKeyString as ThemeKeysEnum,
		)
			? (themeKeyString as ThemeKeysEnum)
			: undefined;
	}

	static generateInterpolatedColors(
		color1: string,
		color2: string,
		n: number = 1,
		bias: number = 0,
	): string[] {
		const biasNormalizer = 30;
		const biasDirection = bias < 0 ? -1 : 1;

		try {
			const oklab1 = ThemeUtils.hexToOklab(color1);
			const oklab2 = ThemeUtils.hexToOklab(color2);
			const colors: string[] = [];
			for (let i = 0; i < n; i++) {
				const t = (i + 1) / (n + 1);
				const adjustedT =
					t + biasDirection * (bias / biasNormalizer) ** 2 * t * (1 - t);
				const clampedT = Math.min(Math.max(adjustedT, 0), 1);

				const L = oklab1.L + clampedT * (oklab2.L - oklab1.L);
				const a = oklab1.a + clampedT * (oklab2.a - oklab1.a);
				const b = oklab1.b + clampedT * (oklab2.b - oklab1.b);
				const alpha = oklab1.alpha + clampedT * (oklab2.alpha - oklab1.alpha);

				const hex = ThemeUtils.oklabToHex(L, a, b, alpha);
				colors.push(hex);
			}
			return colors;
		} catch (e) {
			console.error('Error generating interpolated colors:', e);
			return [];
		}
	}
}
